import type { LicenceInput, LicenceKind, LicencePortKey, ResellerLicence } from "@xop-any-time/shared";
import {
  formatLicenceDate,
  hardwareAddressProblem,
  humaniseLicenceKey,
  LICENCE_LABEL_MAX,
  LICENCE_PORT_KEYS,
  legacyLicenceKeys,
  licenceCatalogSections,
  licenceRenewalNext,
  normalizeHardwareAddress,
  portGateErrors,
  portGateWarnings,
  suggestLicenceLabel,
} from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, Download, Hourglass, KeyRound, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type JSX } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { usePermissions } from "../../../auth/usePermissions.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { isPermissionDenied } from "../../../lib/api.js";
import {
  createLicence,
  deleteLicence,
  getLicence,
  getLicenceCatalog,
  getLicenceDownloads,
  getTrialAvailability,
  issueLicence,
  keys,
  renewLicence,
  saveLicence,
} from "../../../lib/crmApi.js";
import { PageHeader } from "../crm.js";
import { defaultTrialEnd, TRIAL_DAYS } from "./CreateLicenceDialog.js";
import { LicenceFeaturePicker } from "./LicenceFeaturePicker.js";
import { PortCountField } from "./PortCountField.js";
import { takeLicenceFile } from "./licence-ui.js";

/**
 * The licence builder — one appliance: its address, its TYPE, its term, its
 * capacity and the features it may run.
 *
 * ONE PRESS. You pick the terms and press the one button; the licence is
 * created and either signed or sent for approval in the same step. There is no
 * Save, no Draft, no half-made licence: if crm-api refuses a brand-new one, the
 * row is removed again.
 *
 * TWO TYPES, CHOSEN, NOT INFERRED (CEO, 2026-09-15). The Appliance card offers
 * two option cards:
 *  - **Trial licence** — 30 days, generated and downloadable the moment you
 *    press Generate. Only for a customer that holds NO other licence: a new
 *    licence asks `GET /api/crm/licences/trial-availability?customerId=`, an
 *    existing one reads `trial.available`. Unavailable, the card is disabled and
 *    says why. The end date is not editable — it is today + 30.
 *  - **Full licence** — any term or perpetual, and ALWAYS sent for approval,
 *    whatever its length. XOP's executives are emailed; the "Licence file"
 *    card shows a waiting state until one of them approves, then the download.
 * The approvers are always called "XOP's executives" in this UI — never an
 * admin, never a job title.
 * A new licence defaults to Trial when it is available, otherwise Full; a
 * Duplicate is Full unless availability says a trial is possible. `kind` is
 * sent on create/update and is part of the terms an approval is FOR. crm-api
 * refuses a trial the customer may not have (`LICENCE_TRIAL_UNAVAILABLE`), so
 * the cards are advice, not the gate.
 *
 * THE LABEL IS A NAME, AND IT ARRIVES WRITTEN (2026-09-16). The Appliance card
 * asks for a short **Label** between the type cards and the hardware address —
 * what the licences list, the card waiting for XOP's executives and the mail
 * that reaches them call this licence. It is PREFILLED from the customer this
 * appliance belongs to (`suggestLicenceLabel`, shared with the command center)
 * and follows the TYPE while nobody has typed; once the reseller edits it — or
 * when the licence already carries a label of its own — `labelTouched` stops
 * anything overwriting it, and a Duplicate re-suggests rather than copying so
 * two rows never share a name. A licence that was never named — one made before
 * the label came back, or a renewal successor whose predecessor had none —
 * opens with the suggestion rather than an empty box. Nothing derives meaning
 * from a label: it is not in `signedTerms`, so renaming a licence voids no
 * approval — which is why Generate saves when the LABEL alone has changed, not
 * only when the terms have. NOTES stay gone (2026-09-15) — the builder neither
 * shows nor overwrites them.
 *
 * GENERATED MEANS LOCKED. A licence with a file is what a customer holds;
 * editing it would make the row disagree with the file (crm-api refuses too —
 * `LICENCE_LOCKED`). Another machine or different terms is a NEW licence:
 * **Duplicate** prefills this builder from a generated one with the hardware
 * address cleared.
 *
 * THE MOST VALUABLE THING ON THIS SCREEN IS A REFUSAL. `Feature.read_license`
 * force-disables `conferencing` and `mass_notification` when
 * `ports.audio_conferencing` is zero and reports that NOWHERE — the licence
 * installs cleanly and the feature is simply off. `portGateErrors` (shared, so
 * crm-api and both portals agree) blocks Generate and says what would happen.
 * `web_conferencing` only warns: the appliance never gates it.
 *
 * THE HARDWARE ADDRESS IS NORMALISED, NEVER LOOSENED (2026-09-15). The
 * appliance compares `address:` as an exact string with what it reports — a
 * lowercase system id or an uppercase colon MAC — before the signature. The
 * field accepts any standard notation (`AA-BB-CC-DD-EE-FF`, `aabb.ccdd.eeff`,
 * bare hex), shows the shared rule's own sentence when it cannot be used
 * (`hardwareAddressProblem`: broadcast, multicast, all zeros, mixed separators),
 * rewrites itself to the canonical form on blur, and the create/update payload
 * always carries `normalizeHardwareAddress`. The BFF normalises again.
 *
 * ONE SUBMIT, TWICE. The page is a `<form>`: "Create Licence" is a real submit
 * button at the top right AND the bottom right (`licence-generate-top` /
 * `licence-generate-bottom`), same disabled state, and Enter in a field submits
 * when creating is allowed. The label no longer says what will happen — the
 * Trial/Full cards do. When it is disabled the bottom bar lists WHY
 * (`licence-generate-blockers`), and the top button carries the same as its
 * title.
 *
 * RENEWAL (2026-09-15, later). An ISSUED licence can be renewed from 30 days
 * before it expires: **Renew licence** asks crm-api to draft the SUCCESSOR — a
 * new full licence for the same appliance, same features and ports — and lands
 * on its page, where the term is chosen and Create Licence sends it to XOP's
 * executives like any full licence. crm-api decides everything about it
 * (`licence.renewal`): before the window the page says when it opens; once a
 * successor exists it links to it and says how far it has got; a refusal is
 * shown in crm-api's own sentence. On the SUCCESSOR the address cannot change
 * (same appliance) and Trial is not on offer — a renewal is always full.
 *
 * NO TOTAL PORTS FIELD. The appliance reads `total` only as a fallback when
 * audio conferencing is unset, so asking for both invited two numbers to
 * disagree. The builder sends `ports_total` equal to audio conferencing.
 *
 * The form is UNCONTROLLED BY THE SERVER after it first loads: `useEffect`
 * seeds it once and nothing re-seeds it, so a background refetch cannot wipe a
 * half-filled form under the reseller's hands.
 */

/** The counters the builder ASKS for. `total` stays in the data model and the
 * wire, but is derived — see `toInput`. */
const EDITABLE_PORT_KEYS = LICENCE_PORT_KEYS.filter((key) => key !== "total");

const PORT_LABELS: Record<Exclude<LicencePortKey, "total">, string> = {
  tdm: "TDM",
  audioConferencing: "Audio conferencing",
  webConferencing: "Web conferencing",
  networkSwitches: "Network switches",
  audioRecording: "Audio recording",
};

const PORT_HELP: Partial<Record<LicencePortKey, string>> = {
  audioConferencing: "Conferencing and mass notification switch off if this is 0.",
};

const PORT_FIELDS: Record<LicencePortKey, keyof LicenceInput> = {
  total: "ports_total",
  tdm: "ports_tdm",
  audioConferencing: "ports_audio_conferencing",
  webConferencing: "ports_web_conferencing",
  networkSwitches: "ports_network_switches",
  audioRecording: "ports_audio_recording",
};

interface Draft {
  /** null until a NEW licence knows whether a trial is available — then the
   * default is decided once and it is the reseller's to change. */
  kind: LicenceKind | null;
  /** A short human NAME for this licence, prefilled from the customer. Nothing
   * derives meaning from it and it is not one of the signed terms. */
  label: string;
  hardwareAddress: string;
  expiresOn: string;
  featureKeys: string[];
  ports: Record<LicencePortKey, number>;
}

function toDraft(licence: ResellerLicence): Draft {
  const kind = licence.kind;
  return {
    kind,
    // A licence somebody NAMED keeps its name. One made before the label came
    // back — or a renewal successor whose predecessor had none — opens with the
    // suggestion rather than an empty box.
    label:
      licence.label ||
      suggestLicenceLabel({
        customerName: licence.customerName,
        kind,
        renewal: Boolean(licence.renewal?.renews),
      }),
    hardwareAddress: licence.hardwareAddress,
    expiresOn: licence.expiresOn ?? "",
    featureKeys: [...licence.featureKeys],
    ports: { ...licence.ports },
  };
}

/** A brand-new licence: nothing chosen, the type decided by availability. The
 * LABEL is the one field that arrives filled in — from the customer whose
 * appliance this is (2026-09-16) — so a row says something without anyone
 * typing. The type is not known yet, so the suffix follows once it is. */
function emptyDraft(customerName: string): Draft {
  return {
    kind: null,
    label: suggestLicenceLabel({ customerName, kind: "full" }),
    hardwareAddress: "",
    expiresOn: defaultTrialEnd(),
    featureKeys: [],
    ports: { total: 0, tdm: 0, audioConferencing: 0, webConferencing: 0, networkSwitches: 0, audioRecording: 0 },
  };
}

/** The terms that reach the signed file — what an approval is FOR. The type is
 * one of them: approving a full licence is not approving a trial. */
function signedTerms(draft: Draft): string {
  return [
    draft.kind ?? "",
    // Compared canonically: typing `aa:bb:…` over a stored `AA:BB:…` is no change.
    normalizeHardwareAddress(draft.hardwareAddress) ?? draft.hardwareAddress,
    draft.expiresOn,
    [...draft.featureKeys].sort().join(","),
    EDITABLE_PORT_KEYS.map((key) => `${key}=${draft.ports[key]}`).join(","),
  ].join("|");
}

function toInput(draft: Draft): LicenceInput {
  // The label is a human name and nothing reads it as data — it is not in
  // `signedTerms`, so editing it never voids an approval. Notes are still not
  // asked for (2026-09-15); a licence that has some keeps them.
  const input: LicenceInput = {
    ...(draft.kind ? { kind: draft.kind } : {}),
    label: draft.label.trim().slice(0, LICENCE_LABEL_MAX),
    // Always the form the appliance reports, whatever notation was typed.
    hardware_address: normalizeHardwareAddress(draft.hardwareAddress) ?? draft.hardwareAddress,
    // A trial always carries its 30-day end. For a full licence an empty field
    // is PERPETUAL, not "unset": the server omits `expires:` entirely for null,
    // and that is what makes the box never lapse.
    expires_on: draft.kind === "trial" ? draft.expiresOn || defaultTrialEnd() : draft.expiresOn || null,
    feature_keys: draft.featureKeys,
  };
  for (const key of EDITABLE_PORT_KEYS) {
    (input as Record<string, unknown>)[PORT_FIELDS[key] as string] = draft.ports[key];
  }
  // `total` is not asked for. The appliance's `licensed_audio_ports` reads it
  // only as a FALLBACK when audio conferencing is unset, so it is sent equal to
  // audio conferencing and can never contradict it.
  input.ports_total = draft.ports.audioConferencing;
  return input;
}

const NOT_REQUIRED: ResellerLicence["approval"] = {
  status: "not_required",
  requestedAt: null,
  requestedBy: null,
  approvedAt: null,
  approvedBy: null,
  note: null,
};

export function LicenceBuilderPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  // `/licences/new` carries no `:id` at all.
  const isNew = id === undefined || id === "new";
  const licenceId = isNew ? NaN : Number(id);
  const fromId = params.get("from") ? Number(params.get("from")) : null;
  const customerParam = Number(params.get("customer"));
  const nameParam = params.get("name");

  // `licences:view` is what got the reseller onto this route; `licences:edit`
  // is what lets them change anything.
  const readOnly = !can("licences:edit");

  const [draft, setDraft] = useState<Draft | null>(null);
  // Has the reseller written their OWN label? Until they have, the suggestion
  // follows the licence type; once they have, nothing overwrites it.
  const [labelTouched, setLabelTouched] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);

  // Generate on `/new` lands on the licence's page with its result in router
  // state. The same element serves both routes, so read it on change.
  useEffect(() => {
    const handed = (location.state as { notice?: string } | null)?.notice;
    if (handed) setNotice(handed);
  }, [location.state]);

  const licenceQuery = useQuery({
    queryKey: keys.licence(licenceId),
    queryFn: () => getLicence(licenceId),
    enabled: !isNew && Number.isFinite(licenceId),
  });

  // Duplicate: the source licence, copied with the address cleared.
  const sourceQuery = useQuery({
    queryKey: keys.licence(fromId ?? 0),
    queryFn: () => getLicence(fromId!),
    enabled: isNew && fromId !== null,
  });

  const catalogQuery = useQuery({
    queryKey: keys.licenceCatalog,
    queryFn: () => getLicenceCatalog(),
  });

  // Every pull of this file, refusals included. Its own query so a failure
  // here cannot take the builder down.
  const downloadsQuery = useQuery({
    queryKey: keys.licenceDownloads(licenceId),
    queryFn: () => getLicenceDownloads(licenceId),
    enabled: !isNew && Number.isFinite(licenceId),
  });

  const licence = isNew ? null : (licenceQuery.data?.licence ?? null);
  const source = sourceQuery.data?.licence ?? null;
  const target = useMemo(() => {
    if (licence) return { customerId: licence.customerId, customerName: licence.customerName };
    if (source) return { customerId: source.customerId, customerName: source.customerName };
    if (isNew && customerParam) return { customerId: customerParam, customerName: nameParam ?? `Customer #${customerParam}` };
    return null;
  }, [licence, source, isNew, customerParam, nameParam]);

  // May this customer still get a trial? A NEW licence asks; an existing one
  // already carries the answer.
  const availabilityQuery = useQuery({
    queryKey: keys.licenceTrialAvailability(target?.customerId ?? 0),
    queryFn: () => getTrialAvailability(target!.customerId),
    enabled: isNew && Boolean(target?.customerId),
  });
  // null = still asking. A failed ask reads as unavailable: offering a trial
  // crm-api would refuse is worse than defaulting to the path that always works.
  const trialAvailable: boolean | null = isNew
    ? availabilityQuery.isSuccess
      ? availabilityQuery.data.available
      : availabilityQuery.isError
        ? false
        : null
    : licence
      ? licence.trial.available
      : null;

  // Seeded ONCE PER ROUTE. The same element serves `/licences/:id` and
  // `/licences/new?from=` (Duplicate) without remounting, so "once" is keyed
  // on the route, not on mount; and a background refetch never re-seeds, so it
  // cannot wipe a half-filled form under the reseller's hands.
  const routeKey = `${isNew ? "new" : licenceId}|${fromId ?? ""}|${customerParam}`;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  useEffect(() => {
    if (seededFor === routeKey) return;
    if (!isNew && licence) {
      setDraft(toDraft(licence));
      // A label already on the record is somebody's choice, not a suggestion:
      // switching the type must not rename a licence under them.
      setLabelTouched(licence.label.trim() !== "");
      setSeededFor(routeKey);
    } else if (isNew && fromId !== null && source) {
      // A copy decides its own type: Full unless this customer may still trial.
      // The label is re-suggested rather than copied — two rows for the same
      // customer must not carry the same name.
      setDraft({
        ...toDraft(source),
        label: suggestLicenceLabel({ customerName: source.customerName, kind: "full" }),
        hardwareAddress: "",
        kind: null,
      });
      setLabelTouched(false);
      setSeededFor(routeKey);
    } else if (isNew && fromId === null) {
      setDraft(emptyDraft(nameParam ?? ""));
      setLabelTouched(false);
      setSeededFor(routeKey);
    }
  }, [routeKey, seededFor, licence, source, isNew, fromId, nameParam]);

  // A new licence's default type, decided ONCE availability is known: Trial if
  // the customer may have one, otherwise Full. After that it is the reseller's.
  useEffect(() => {
    if (!draft || draft.kind !== null || seededFor !== routeKey || trialAvailable === null) return;
    const kind: LicenceKind = trialAvailable ? "trial" : "full";
    // The suggestion only learns the type here, so it is completed with it —
    // unless the reseller has already written their own label.
    const label =
      labelTouched || !target ? draft.label : suggestLicenceLabel({ customerName: target.customerName, kind });
    setDraft(
      kind === "trial"
        ? { ...draft, kind, label, expiresOn: defaultTrialEnd() }
        : { ...draft, kind, label },
    );
  }, [draft, seededFor, routeKey, trialAvailable, labelTouched, target]);

  const gateErrors = useMemo(
    () => (draft ? portGateErrors({ featureKeys: draft.featureKeys, ports: draft.ports }) : []),
    [draft],
  );
  const gateWarnings = useMemo(
    () => (draft ? portGateWarnings({ featureKeys: draft.featureKeys, ports: draft.ports }) : []),
    [draft],
  );
  const canonicalAddress = draft ? normalizeHardwareAddress(draft.hardwareAddress) : null;
  const addressValid = canonicalAddress !== null;
  const addressProblem = draft ? hardwareAddressProblem(draft.hardwareAddress) : null;

  const describeOutcome = (result: Awaited<ReturnType<typeof issueLicence>>): string => {
    if (result.outcome === "approval_requested") {
      return "Sent for approval. XOP's executives have been notified; the download appears here once they approve it.";
    }
    if (result.outcome === "already_pending") return "Already waiting for approval — nothing was re-sent.";
    // The stored TYPE decides, never the date: a full licence with a short
    // term is still a full licence.
    return result.licence.kind === "trial"
      ? "Trial licence generated — download it below."
      : "Licence generated — download it below.";
  };

  /** Generate: create (or update) the row, then sign or send — one press. */
  const generate = useMutation({
    mutationFn: async () => {
      if (!draft || !target) throw new Error("Nothing to generate.");
      if (isNew) {
        const { licence: created } = await createLicence({ ...toInput(draft), customer_id: target.customerId });
        try {
          return { created, result: await issueLicence(created.id) };
        } catch (err) {
          // No half-made licence: the row was only ever a means to a file.
          await deleteLicence(created.id).catch(() => undefined);
          throw err;
        }
      }
      // The label is deliberately NOT a signed term — renaming a licence voids
      // no approval — which is exactly why the terms check alone would drop a
      // rename. Save when either has changed.
      if (
        licence &&
        (signedTerms(draft) !== signedTerms(toDraft(licence)) || draft.label.trim() !== licence.label)
      ) {
        await saveLicence(licenceId, toInput(draft));
      }
      return { created: null, result: await issueLicence(licenceId) };
    },
    onSuccess: async ({ created, result }) => {
      await queryClient.invalidateQueries({ queryKey: ["licences"] });
      if (created) {
        navigate(`/licences/${created.id}`, { replace: true, state: { notice: describeOutcome(result) } });
        return;
      }
      setNotice(describeOutcome(result));
      setFailure(null);
      setWarnings(result.warnings.map((warning) => warning.message));
      setDraft(toDraft(result.licence));
      await queryClient.invalidateQueries({ queryKey: keys.licence(licenceId) });
      await queryClient.invalidateQueries({ queryKey: keys.licenceDownloads(licenceId) });
    },
    // The refusal's sentence is the whole point — a licence that would install
    // cleanly and not work is only ever caught here.
    onError: (err: Error) => setFailure(err.message),
  });

  /** Renew: draft the successor and go there. The term is chosen on ITS page. */
  const renew = useMutation({
    mutationFn: () => renewLicence(licenceId),
    onSuccess: async ({ licence: successor }) => {
      await queryClient.invalidateQueries({ queryKey: ["licences"] });
      await queryClient.invalidateQueries({ queryKey: keys.licence(licenceId) });
      const serial = licence?.currentIssue?.serial ?? successor.renewal.renews?.serial ?? `#${licenceId}`;
      navigate(`/licences/${successor.id}`, {
        state: {
          notice: `Renewal drafted from ${serial} — choose the term and press Create Licence; XOP's executives will need to approve it.`,
        },
      });
    },
    // crm-api's sentence — "This licence can be renewed from Oct 1, 2026 …" —
    // is the whole answer; nothing here rewrites it.
    onError: (err: Error) => setFailure(err.message),
  });

  async function take(): Promise<void> {
    if (!licence) return;
    setDownloading(true);
    setFailure(null);
    try {
      await takeLicenceFile(licence);
      await queryClient.invalidateQueries({ queryKey: keys.licence(licenceId) });
    } catch (err) {
      setFailure((err as Error).message);
    } finally {
      setDownloading(false);
      // Allowed or refused, the pull is in the journal now.
      await queryClient.invalidateQueries({ queryKey: keys.licenceDownloads(licenceId) });
    }
  }

  if (isPermissionDenied(licenceQuery.error) || isPermissionDenied(sourceQuery.error)) {
    const denied = isPermissionDenied(licenceQuery.error) ? licenceQuery.error : sourceQuery.error;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Licence" description="One appliance." />
        <NoAccessPanel permission={(denied as { permission?: string })?.permission ?? "licences:view"} />
      </Box>
    );
  }

  if (isNew && !fromId && !customerParam) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="New licence" description="Pick a customer first." />
        <Button component={Link} to="/licences" size="small" startIcon={<ArrowLeft size={15} />} sx={{ alignSelf: "flex-start" }}>
          Licences
        </Button>
      </Box>
    );
  }

  if (!draft || !target || seededFor !== routeKey) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  const issued = licence?.currentIssue ?? null;
  const approval = licence?.approval ?? NOT_REQUIRED;
  const pending = approval.status === "pending";
  // Generated or awaiting a decision: nothing here may change. Duplicate instead.
  const locked = readOnly || Boolean(issued) || pending;
  const kind = draft.kind;
  // A SUCCESSOR: this licence renews another one. Same appliance, always full.
  const renews = licence?.renewal.renews ?? null;
  const isRenewal = renews !== null;
  const renewalNext = licence && issued ? licenceRenewalNext(licence) : null;
  const canRenew = Boolean(licence?.renewal.renewable) && !readOnly && !renew.isPending;
  // A licence that already IS a trial keeps its own type selectable; otherwise
  // the customer must hold no other licence — and a renewal is never a trial.
  const trialSelectable = !isRenewal && (trialAvailable === true || licence?.kind === "trial");
  const dirty = licence ? signedTerms(draft) !== signedTerms(toDraft(licence)) : true;
  const approvedForTheseTerms = approval.status === "approved" && !dirty;
  // WHY the button is disabled, built from the same checks that disable it.
  const blockers: string[] = [];
  if (kind === null) blockers.push("Choose a licence type");
  else if (kind === "trial" && !trialSelectable) blockers.push("A trial is not available for this customer — choose a full licence");
  if (draft.hardwareAddress.trim() === "") blockers.push("Enter the hardware address");
  else if (addressProblem) blockers.push(addressProblem);
  blockers.push(...gateErrors);
  const canGenerate = !locked && blockers.length === 0 && addressValid && !generate.isPending;
  const showBlockers = !locked && !generate.isPending && blockers.length > 0;
  const downloads = downloadsQuery.data?.downloads ?? [];

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (canGenerate) generate.mutate();
  }

  /** The one primary action, rendered at the top and the bottom of the page. */
  const createButton = (position: "top" | "bottom"): JSX.Element => (
    <Button
      type="submit"
      variant="contained"
      startIcon={<KeyRound size={15} />}
      disabled={!canGenerate}
      data-testid={`licence-generate-${position}`}
      {...(showBlockers ? { "aria-describedby": "licence-generate-blockers" } : {})}
    >
      {generate.isPending ? "Creating…" : "Create Licence"}
    </Button>
  );

  function chooseKind(next: LicenceKind): void {
    // `target` is non-null by the time this can be pressed; the check is what
    // narrows it inside this closure.
    if (!draft || !target || draft.kind === next) return;
    // The suggested label follows the type until somebody types their own;
    // after that it is theirs.
    const label = labelTouched
      ? draft.label
      : suggestLicenceLabel({ customerName: target.customerName, kind: next });
    if (next === "trial") {
      if (isRenewal) return;
      // A trial's end is not the reseller's to pick: today + 30, or — for a
      // licence that already is a trial — the end it was saved with.
      const end = licence?.kind === "trial" && licence.expiresOn ? licence.expiresOn : defaultTrialEnd();
      setDraft({ ...draft, kind: "trial", label, expiresOn: end });
    } else {
      setDraft({ ...draft, kind: "full", label });
    }
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={submit}
      aria-label="Licence builder"
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Button
        component={Link}
        to="/licences"
        size="small"
        startIcon={<ArrowLeft size={15} />}
        sx={{ alignSelf: "flex-start" }}
      >
        Licences
      </Button>

      <PageHeader
        title={`${target.customerName} — ${isNew ? "New licence" : licence?.label || draft.hardwareAddress || "Appliance"}`}
        description={
          issued
            ? "What this appliance may run. A generated licence is locked — duplicate it to license another machine or different terms."
            : "What this appliance may run. Pick the terms and generate the file in one step. Distinct from the agreement, which is what the customer bought."
        }
        actions={
          licence && issued && !readOnly ? (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {licence.renewal.renewable && (
                <Button
                  variant="contained"
                  startIcon={<RefreshCw size={15} />}
                  disabled={!canRenew}
                  onClick={() => renew.mutate()}
                  data-testid="licence-renew"
                >
                  {renew.isPending ? "Drafting…" : "Renew licence"}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<Copy size={15} />}
                onClick={() => navigate(`/licences/new?from=${licence.id}`)}
              >
                Duplicate
              </Button>
            </Box>
          ) : !locked ? (
            // A disabled button fires no pointer events, so the reason rides on
            // a wrapper's title.
            <Box component="span" title={showBlockers ? blockers.join("\n") : undefined} sx={{ display: "inline-flex" }}>
              {createButton("top")}
            </Box>
          ) : undefined
        }
      />

      {readOnly && (
        <Alert severity="info">
          You can view this licence. Ask an owner for licences:edit to change licences.
        </Alert>
      )}
      {failure && (
        <Alert severity="error" onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {renews && (
        <Alert severity="info" data-testid="licence-renews">
          Renews{" "}
          <MuiLink component={Link} to={`/licences/${renews.id}`} underline="hover">
            {renews.serial ?? `#${renews.id}`}
          </MuiLink>
          {renews.expiresOn ? `, which expires ${formatLicenceDate(renews.expiresOn)}` : ""}. Same appliance — the
          address cannot change.
        </Alert>
      )}
      {approvedForTheseTerms && !issued && (
        <Alert severity="info">
          Approved by XOP&apos;s executives on {approval.approvedAt?.slice(0, 10) ?? "—"} for exactly these terms.
          Changing any of them needs a new approval.
        </Alert>
      )}
      {gateErrors.map((message) => (
        <Alert key={message} severity="error">
          {message}
        </Alert>
      ))}
      {[...gateWarnings, ...warnings].map((message) => (
        <Alert key={message} severity="warning">
          {message}
        </Alert>
      ))}

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
        }}
      >
        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Appliance</Typography>
            {/* The TYPE first (2026-09-15): it decides whether the term below is
                a fixed 30 days or a date to choose. */}
            <LicenceTypeSelector
              kind={kind}
              trialSelectable={trialSelectable}
              trialKnown={trialAvailable !== null || licence?.kind === "trial" || isRenewal}
              renewal={isRenewal}
              disabled={locked}
              onChoose={chooseKind}
            />
            {/* The LABEL next (2026-09-16): a short name, prefilled from the
                customer, so the list, the approval card and the mail to XOP's
                executives say WHICH licence they are about. It is not one of
                the signed terms — renaming a licence voids no approval. */}
            <TextField
              size="small"
              label="Label"
              value={draft.label}
              disabled={locked}
              inputProps={{ maxLength: LICENCE_LABEL_MAX }}
              onChange={(event) => {
                setLabelTouched(true);
                setDraft({ ...draft, label: event.target.value });
              }}
              helperText="A short name for this licence — what the list, the approval card and the mail to XOP's executives call it. Prefilled from the customer; write whatever you recognise."
            />
            <TextField
              size="small"
              label="Hardware address"
              value={draft.hardwareAddress}
              // A renewal is the SAME appliance: the address is the one thing
              // that cannot change.
              disabled={locked || isRenewal}
              error={addressProblem !== null}
              onChange={(event) => setDraft({ ...draft, hardwareAddress: event.target.value })}
              // Show what will be saved: the notation the appliance reports.
              onBlur={() => {
                if (canonicalAddress && canonicalAddress !== draft.hardwareAddress) {
                  setDraft({ ...draft, hardwareAddress: canonicalAddress });
                }
              }}
              helperText={
                addressProblem ??
                (isRenewal
                  ? "The renewed appliance's address — a renewal cannot move to another machine."
                  : "Its system id or MAC address — any standard notation (AA:BB:CC:DD:EE:FF, AA-BB-CC-DD-EE-FF, aabb.ccdd.eeff). Saved as the appliance reports it.")
              }
            />
            {kind === "trial" ? (
              // A trial's term is fixed — shown, not asked.
              <Box data-testid="licence-trial-end">
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Expires</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  Ends {formatLicenceDate(draft.expiresOn || defaultTrialEnd())} · {TRIAL_DAYS} days
                </Typography>
              </Box>
            ) : (
              <TextField
                size="small"
                type="date"
                label="Expires"
                value={draft.expiresOn}
                disabled={locked || kind === null}
                onChange={(event) => setDraft({ ...draft, expiresOn: event.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText={
                  draft.expiresOn
                    ? "The term ends at the end of this day."
                    : "Empty means PERPETUAL — the licence file carries no expiry at all."
                }
              />
            )}
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Card>
            <CardContent>
              <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 2 }}>Ports</Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                }}
              >
                {EDITABLE_PORT_KEYS.map((key) => (
                  <PortCountField
                    key={key}
                    size="small"
                    label={PORT_LABELS[key as Exclude<LicencePortKey, "total">]}
                    value={draft.ports[key]}
                    disabled={locked}
                    helperText={PORT_HELP[key] ?? " "}
                    onValueChange={(value) =>
                      setDraft((current) =>
                        current ? { ...current, ports: { ...current.ports, [key]: value } } : current,
                      )
                    }
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {issued ? (
            <Card>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Licence file</Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {issued.serial} · generated {issued.issuedAt.slice(0, 10)} by {issued.issuedByLabel}
                </Typography>
                <Typography
                  sx={{ fontSize: 11, color: "text.secondary", fontFamily: "monospace" }}
                >
                  sha256 {issued.sha256.slice(0, 16)}…
                </Typography>
                {issued.downloadLimit !== null && (
                  <Typography sx={{ fontSize: 13 }}>
                    {issued.downloadsRemaining} of {issued.downloadLimit} downloads left (capped by XOP)
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Download size={15} />}
                  disabled={downloading}
                  onClick={() => void take()}
                  sx={{ alignSelf: "flex-start", mt: 1 }}
                >
                  {downloading ? "Downloading…" : "Download"}
                </Button>
                {renewalNext && renewalNext.kind !== "renewable" && (
                  <Typography
                    component="div"
                    sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}
                    data-testid={renewalNext.kind === "opens" ? "licence-renewal-opens" : "licence-renewal-successor"}
                  >
                    {renewalNext.kind === "opens" ? (
                      `Renewal opens ${formatLicenceDate(renewalNext.on)} (${licence?.renewal.windowDays ?? 30} days before expiry)`
                    ) : (
                      <MuiLink component={Link} to={`/licences/${renewalNext.successorId}`} underline="hover">
                        Renewal {renewalNext.serial} ·{" "}
                        {renewalNext.kind === "generated"
                          ? "generated"
                          : renewalNext.kind === "pending"
                            ? "waiting for approval"
                            : renewalNext.kind === "rejected"
                              ? "not approved"
                              : "drafted"}
                      </MuiLink>
                    )}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ) : pending ? (
            <Card data-testid="licence-awaiting-approval">
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Licence file</Typography>
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderStyle: "dashed",
                    borderColor: alpha(theme.palette.warning.main, 0.6),
                    bgcolor: alpha(theme.palette.warning.main, 0.06),
                  })}
                >
                  <Box component="span" sx={{ color: "warning.main", display: "inline-flex", mt: 0.25 }}>
                    <Hourglass size={18} aria-hidden="true" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      Waiting for approval — the download appears here once XOP&apos;s executives approve this
                      licence.
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
                      Awaiting approval by XOP&apos;s executives. Requested by {approval.requestedBy ?? "—"} on{" "}
                      {approval.requestedAt?.slice(0, 10) ?? "—"}; the terms are locked until then.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : approval.status === "rejected" ? (
            <Card data-testid="licence-not-approved">
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Licence file</Typography>
                <Alert severity="error">
                  <strong>Not approved by XOP&apos;s executives</strong> on {approval.approvedAt?.slice(0, 10) ?? "—"}
                  {approval.note ? ` — “${approval.note}”` : "."} Change the terms and send it for approval
                  again.
                </Alert>
              </CardContent>
            </Card>
          ) : null}
        </Box>
      </Box>

      {licence && (
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Downloads</Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>
              Every pull of this licence&apos;s file — who, when, from where. Refusals are recorded too.
            </Typography>
            {downloads.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No downloads yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Who</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {downloads.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {row.downloadedAt.replace("T", " ").slice(0, 16)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.serial}</TableCell>
                      <TableCell>{row.actorLabel}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.ip ?? "—"}</TableCell>
                      <TableCell>
                        {row.refused ? (
                          <Chip size="small" color="error" label="Refused" />
                        ) : (
                          <Chip size="small" variant="outlined" color="success" label="Downloaded" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Features</Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>
            Every feature the appliance can license, in the two sections its own licence
            page shows.
          </Typography>
          {catalogQuery.isPending ? (
            <CircularProgress size={20} />
          ) : (
            // The TWO sections, in the appliance's fixed order — Value Added
            // Services, then Platform Extensions (CEO, 2026-09-15). The
            // grouping is `licenceCatalogSections` (shared): crm-api's
            // `section` decides, a row without one is placed by its category,
            // an empty section is not drawn, and no feature is lost.
            <LicenceFeaturePicker
              sections={licenceCatalogSections(catalogQuery.data?.features ?? [])}
              selectedKeys={draft.featureKeys}
              // A key on the licence that no Pricing row links: shown greyed
              // and selected, named by the licence's own row or humanised,
              // and only ever switched OFF.
              legacy={legacyLicenceKeys(draft.featureKeys, catalogQuery.data?.features ?? []).map((key) => ({
                key,
                name:
                  (licence ?? source)?.features.find((feature) => feature.key === key)?.name || humaniseLicenceKey(key),
              }))}
              disabled={locked}
              onToggle={(key) =>
                setDraft({
                  ...draft,
                  featureKeys: draft.featureKeys.includes(key)
                    ? draft.featureKeys.filter((selected) => selected !== key)
                    : [...draft.featureKeys, key],
                })
              }
            />
          )}
        </CardContent>
      </Card>

      <Divider />

      {locked ? (
        !readOnly && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {issued
              ? "This licence has been generated and cannot be edited. Use Duplicate for another machine or different terms."
              : "Waiting for XOP's executives to decide — the terms are locked until then."}
          </Typography>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {showBlockers && (
            <Box
              component="ul"
              id="licence-generate-blockers"
              data-testid="licence-generate-blockers"
              aria-label="Before you can create this licence"
              sx={{ m: 0, pl: 2.5, fontSize: 13, color: "text.secondary" }}
            >
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </Box>
          )}
          {createButton("bottom")}
        </Box>
      )}
    </Box>
  );
}

/**
 * Trial or Full — two option cards with radio semantics.
 *
 * Each card is a native button carrying `role="radio"` inside a radiogroup, so
 * it is keyboard-operable and a real disabled state. The accessible NAME is the
 * type alone ("Trial licence"); the caption and body describe it.
 */
function LicenceTypeSelector({
  kind,
  trialSelectable,
  trialKnown,
  renewal = false,
  disabled,
  onChoose,
}: {
  kind: LicenceKind | null;
  trialSelectable: boolean;
  /** False while availability is still being asked. */
  trialKnown: boolean;
  /** A successor: a renewal is always a full licence. */
  renewal?: boolean;
  disabled: boolean;
  onChoose: (kind: LicenceKind) => void;
}): JSX.Element {
  const options: Array<{
    value: LicenceKind;
    title: string;
    caption: string;
    body: string;
    unavailable: boolean;
  }> = [
    {
      value: "trial",
      title: "Trial licence",
      caption: "Available immediately",
      body: renewal
        ? "A renewal is always a full licence"
        : trialKnown && !trialSelectable
          ? "Not available — this customer already has a licence."
          : `${TRIAL_DAYS} days. The file is generated and ready to download as soon as you press Generate.`,
      unavailable: renewal || !trialSelectable,
    },
    {
      value: "full",
      title: "Full licence",
      caption: "Needs XOP approval",
      body: "Any term, or perpetual. XOP's executives are notified, and the download appears here once they approve it.",
      unavailable: false,
    },
  ];

  return (
    <Box>
      <Typography id="licence-type-label" sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
        Licence type
      </Typography>
      <Box
        role="radiogroup"
        aria-labelledby="licence-type-label"
        sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}
      >
        {options.map((option) => {
          const checked = kind === option.value;
          const bodyId = `licence-type-${option.value}-body`;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={checked}
              aria-label={option.title}
              aria-describedby={bodyId}
              disabled={disabled || option.unavailable}
              onClick={() => onChoose(option.value)}
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                textAlign: "left",
                gap: 0.5,
                p: 1.75,
                borderRadius: 1,
                border: checked ? 2 : 1,
                // Keep the content from shifting when the border thickens.
                m: checked ? 0 : "1px",
                borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                bgcolor: checked ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                transition: "border-color 120ms, background-color 120ms",
                "&:hover": { borderColor: checked ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.3) },
                "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                "&.Mui-disabled": { opacity: checked ? 0.85 : 0.55 },
              })}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={(theme) => ({
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: `2px solid ${checked ? theme.palette.primary.main : theme.palette.text.disabled}`,
                    display: "grid",
                    placeItems: "center",
                    "&::after": checked
                      ? { content: '""', width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }
                      : {},
                  })}
                />
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
                  {option.title}
                </Typography>
              </Box>
              <Typography
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: option.value === "trial" ? "success.main" : "warning.main",
                }}
              >
                {option.caption}
              </Typography>
              <Typography id={bodyId} component="span" sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {option.body}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

export default LicenceBuilderPage;
